import { useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { errorMsg, validateDecimal } from '../../../../../../../Apis/functions';
import { permissionObj } from '../../../../../../../Helpers/permission';
import routes from '../../../../../../../Routes/routes';
import { TABS_ATTENDANCE, ATTENDANCE_SUBVIEW_MODEL } from '../../../Model/Model';
import { CreateUserDeductionPolicy } from '../../../../../../../reduxToolkit/DeductionPolicy/DeductionPolicyApi';
import {
    DEDUCTION_POLICY_TAB,
    STEPS_ADD_USER_DEDUCTION_POLICY,
    USER_DEDUCTION_ENTRY_MODEL,
    USER_DEDUCTION_POLICY_MODEL
} from '../../DeductionPolicy/Model/Model';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @Description Logic hook for the Create User Deduction Policy stepper.
 * Manages user selection, deduction entries, excluded global rules,
 * step validation and form submission.
 */
const CreateUserDeductionPolicyApp = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const DEV = useSelector((s) => s?.CommonsSlice?.development);
    const { permission } = useSelector((s) => s.authenticatioauthennSlice);
    const canCreate = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_DEDUCTION_POLICY_USER_CREATE);

    const [steps, setSteps] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [emptyFields, setEmptyFields] = useState(false);

    const [user, setUser] = useState([]);
    const [cleanChipSearch, setCleanChipSearch] = useState(false);
    const [deductions, setDeductions] = useState([{ ...USER_DEDUCTION_ENTRY_MODEL, _key: Date.now() }]);
    const [excludedGlobalRules, setExcludedGlobalRules] = useState([]);
    const [selectForDelete, setSelectForDelete] = useState([]);
    const [searchElement, setSearchElement] = useState('');
    const [modified, setModified] = useState(false);

    useLayoutEffect(() => {
        const stepArray = Object.values(STEPS_ADD_USER_DEDUCTION_POLICY)
            .filter(s => s?.TRANS && typeof s.INDEX === 'number')
            .sort((a, b) => a.INDEX - b.INDEX)
            .map(s => t(s.TRANS));
        setSteps(stepArray);
    }, [t]);

    const handleStepChange = (step) => setActiveStep(step);

    /**
     * Per-step validation for Next button clicks.
     * @param {number} step - Active step index.
     * @returns {boolean}
     */
    const handleValidateSteps = (step) => {
        if (step === STEPS_ADD_USER_DEDUCTION_POLICY.SELECT_USER.INDEX) {
            if (user.length === 0) {
                setEmptyFields(true);
                return false;
            }
        }else if (step === STEPS_ADD_USER_DEDUCTION_POLICY.DEDUCTION_POLICY.INDEX) {
            const hasInvalid = deductions.some(d => !d.isValid() || !validateDecimal(d.value).isValid || !d.isValidDateRange());
            if (deductions.length > 0 && hasInvalid) {
                setEmptyFields(true);
                toast.warn(t('please_fill_all_fields'));
                return false;
            }
            if (deductions.some(d => d.isDuplicateName(deductions))) {
                setEmptyFields(true);
                toast.warn(t('payroll_deduction_user_item_duplicate'));
                return false;
            }
        }
        setEmptyFields(false);
        return true;
    };

    /**
     * Clears the data owned by a specific step.
     * @param {number} step - Step index.
     */
    const handleClearStep = (step) => {
        const clearMap = {
            [STEPS_ADD_USER_DEDUCTION_POLICY.SELECT_USER.INDEX]: () => {
                setUser([]);
                setCleanChipSearch(true);
            },
            [STEPS_ADD_USER_DEDUCTION_POLICY.DEDUCTION_POLICY.INDEX]: () =>
                setDeductions([{ ...USER_DEDUCTION_ENTRY_MODEL, _key: Date.now() }]),
            [STEPS_ADD_USER_DEDUCTION_POLICY.EXCLUDED_GLOBAL_DEDUCTIONS.INDEX]: () => setExcludedGlobalRules([]),
        };
        clearMap[step]?.();
        setEmptyFields(false);
    };

    /**
     * Builds the API body and submits the new user deduction policy assignment.
     */
    const handleSubmit = () => {
        if (user.length === 0 || deductions.filter(e => !e.isValid()).length > 0) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }
        const body = USER_DEDUCTION_POLICY_MODEL.toRegister(user, deductions, excludedGlobalRules);
        dispatch(CreateUserDeductionPolicy(body)).then(({ payload: { data: { success, code } } }) => {
            if (success) {
                toast.success(t('has_been_created_successfully'));
                handleOnBack();
            } else errorMsg(code);
        });
    };

    const handleOnBack = () => {
        navigate(routes.attendance.main, {
            state: {
                currentTab: TABS_ATTENDANCE.MANAGE.INDEX,
                currentSubView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.DEDUCTION_POLICY,
                currentSubViewChild: DEDUCTION_POLICY_TAB.USER.key,
            },
        });
    };

    return {
        t,
        canCreate,
        steps,
        activeStep, setActiveStep,
        handleStepChange,
        emptyFields, setEmptyFields,
        handleValidateSteps,
        handleClearStep,
        handleOnBack,
        user, setUser,
        cleanChipSearch, setCleanChipSearch,
        deductions, setDeductions,
        excludedGlobalRules, setExcludedGlobalRules,
        selectForDelete, setSelectForDelete,
        searchElement, setSearchElement,
        modified, setModified,
        handleSubmit,
    };
};

export default CreateUserDeductionPolicyApp;
